/**
 * Legacy API Client
 * 
 * **DEPRECATED - For migration tracking only**
 * 
 * This client handles API endpoints that still return old response formats.
 * Use this ONLY for endpoints that haven't been migrated to the new
 * ApiResponse<T> format yet.
 * 
 * Once an endpoint is migrated on the server, move its usage to `apiClient.ts`
 * and remove the import from `legacyApiClient`.
 * 
 * ## Migration Checklist
 * 
 * For each endpoint using legacyApiClient:
 * 1. [ ] Server endpoint updated to return new ApiResponse<T> format
 * 2. [ ] Domain service updated to use `apiClient` instead of `legacyApiClient`
 * 3. [ ] React Query hooks updated to handle new response format
 * 4. [ ] Components updated to check `result.success` instead of assuming success
 * 5. [ ] Tests updated for new format
 * 6. [ ] Thoroughly tested in staging
 * 7. [ ] Remove legacyApiClient import from the service
 * 
 * ## Endpoint Migration Status
 * 
 * Track which endpoints have been migrated:
 * 
 * ```
 * ### Club Domain
 * GET /clubs/:id                        ❌ LEGACY - Returns club directly with tenant
 * GET /clubs/:id/status                 ❌ LEGACY - Returns status object directly
 * GET /clubs/:id/payment-settings       ❌ LEGACY - Returns settings object directly
 * 
 * ### Booking Domain (TODO)
 * GET /bookings                         ❌ LEGACY - Still returns { items: [], total: N }
 * GET /bookings/:id                     ❌ LEGACY - Still returns { data: Booking }
 * POST /bookings                        ❌ LEGACY - Still returns { data: Booking }
 * PATCH /bookings/:id                   ❌ LEGACY - Still returns { data: Booking }
 * DELETE /bookings/:id                  ❌ LEGACY - Still returns { success: true }
 * 
 * Update this as endpoints are migrated!
 * ```
 * 
 * @deprecated - Use `apiClient` instead for new code
 * @see apiClient.ts for the new client
 * 
 * @example
 * ```typescript
 * // OLD - Legacy endpoint returning { items: [...], total: N }
 * const result = await legacyApiClient.get<LegacyPaginatedResponse<Booking>>('/bookings');
 * if (result.success === undefined) { // No success field in legacy format
 *   const { items, total } = result as LegacyPaginatedResponse<Booking>;
 *   // Use items and total
 * }
 * 
 * // NEW - Migrated endpoint returning { success: true, payload: [...] }
 * const result = await apiClient.get<Booking[]>('/bookings');
 * if (result.success) {
 *   // Use result.payload
 * }
 * ```
 */

import { isSessionRejected, statusOf } from '~/services/auth/api/sessionFailure';
import { API_CONFIG, RETRY_CONFIG, ERROR_CODES, IS_PRODUCTION } from './constants';
import { ApiError } from './errors';
import type { LegacyResponse, RequestOptions } from './types';
import { buildHeaders, buildVersionedUrl } from './utils/headers';
import { createTimeoutController, isTimeoutError as isTimeoutErrorUtil } from './utils/timeout';
import { deduplicateRequest, generateRequestKey } from './utils/requestDedup';
import { withRetry, isRetryableStatus } from './utils/retryLogic';
import { logErrorToMonitoring, extractMonitoringContext } from './utils/monitoring';
import { getTenantHeaders } from './utils/getTenantHeaders';
import { getAccessToken } from '~/services/auth/storage/tokenStorage';
import { shouldRefreshToken } from '~/services/auth/utils/jwt';
import { refreshAccessToken, isRefreshInProgress, queuePendingRequest } from '~/services/auth/api/tokenRefresh';

// ============================================================================
// Token Refresh Integration
// ============================================================================

/**
 * Ensure token is valid before making request
 * 
 * Uses auth service's token refresh logic with queue management.
 * If refresh is already in progress, waits for it to complete.
 * 
 * @throws ApiError if token refresh fails
 */
async function ensureTokenValid(): Promise<void> {
  const token = getAccessToken();

  // Public endpoints don't need token validation
  if (!token) {
    return;
  }

  // Check if token needs refresh (within 5 min buffer)
  if (shouldRefreshToken(token)) {
    // If another request is already refreshing, wait for it
    if (isRefreshInProgress()) {
      await queuePendingRequest();
    } else {
      // Start refresh
      try {
        await refreshAccessToken();
      } catch (error) {
        // Preserve WHY the refresh failed. Re-labelling everything as a 401 told the
        // rest of the app "session expired" even when the truth was "the provider is
        // down", which is how an outage turned into a forced logout.
        if (isSessionRejected(error)) {
          throw new ApiError('Session expired', ERROR_CODES.UNAUTHORIZED, {
            status: 401,
            userMessage: 'Your session has expired. Please log in again.',
          });
        }
        throw new ApiError('Could not refresh session', ERROR_CODES.EXTERNAL_SERVICE_ERROR, {
          status: statusOf(error) ?? 0,
          userMessage: 'We could not reach the server. Please try again in a moment.',
          details: {
            causeStatus: statusOf(error),
            causeCode: (error as { code?: string } | undefined)?.code,
            online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
          },
        });
      }
    }
  }
}

// ============================================================================
// Request Pipeline (Legacy)
// ============================================================================

/**
 * Execute HTTP request for legacy endpoints
 * 
 * Same pipeline as apiClient but:
 * - Does NOT transform response format
 * - Returns raw legacy response data
 * - No automatic error wrapping
 * 
 * @internal - Use get/post/put/patch/delete methods instead
 */
async function request<T extends LegacyResponse<unknown>>(
  method: string,
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<T> {
  const startTime = Date.now();
  const httpMethod = method.toUpperCase();
  let attempts = 0;
  const maxRetries = options?.retries ?? RETRY_CONFIG.MAX_RETRIES;

  const requestKey = generateRequestKey(
    httpMethod,
    buildVersionedUrl(endpoint, options),
    body ? JSON.stringify(body) : undefined
  );

  const monitoringContext = extractMonitoringContext(options, endpoint, httpMethod);

  try {
    // Refresh once, BEFORE the retry loop and before headers are built. Inside the loop
    // the headers were built first, so the request after a refresh still carried the
    // stale token; and every retry re-ran the refresh, re-presenting a refresh token
    // Supabase may already have rotated (reuse past ~10s revokes the session).
    await ensureTokenValid();

    return await deduplicateRequest(requestKey, async () => {
      return await withRetry(
        async () => {
          attempts++;

          if (!IS_PRODUCTION) {
            console.log(
              `[LegacyApiClient] 🚀 Request attempt ${attempts}/${maxRetries + 1}`,
              `\n  Method: ${httpMethod}`,
              `\n  Endpoint: ${endpoint}`,
              `\n  ⚠️  LEGACY FORMAT`
            );
          }

          // URL Construction
          const url = buildVersionedUrl(endpoint, options);

          // Inject Tenant Context (from appRuntime's URL params, NOT from
          // TenantProvider — that provider carries branding for rendering and has
          // no part in request headers).
          const tenantContext = getTenantHeaders(options);
          const optionsWithTenant: RequestOptions = {
            ...options,
            tenantHandle: tenantContext?.tenantHandle ?? options?.tenantHandle,
            clubId: tenantContext?.clubId ?? options?.clubId,
          };

          // Header Preparation
          const headers = await buildHeaders(optionsWithTenant);

          // Timeout
          const timeout = options?.timeout ?? API_CONFIG.DEFAULT_TIMEOUT_MS;
          const { controller, cleanup: cleanupTimeout } = createTimeoutController(timeout);

          try {
            // Fetch
            const response = await fetch(url, {
              method: httpMethod,
              headers,
              body: body ? JSON.stringify(body) : undefined,
              signal: controller.signal,
            });

            // Parse response
            const responseText = await response.text();
            let json: unknown;
            
            try {
              json = responseText ? JSON.parse(responseText) : null;
            } catch (parseError) {
              // JSON parse error - server sent invalid JSON (likely HTML error page)
              // Retry behavior: Only retries if HTTP status is retryable (5xx/408; never 429) -
              // a 503 with HTML may recover, but a 200 with bad JSON won't
              throw new ApiError(
                'Server returned invalid JSON response',
                ERROR_CODES.PARSE_ERROR,
                {
                  userMessage: 'The server returned an invalid response. Please try again later.',
                  status: response.status,
                  details: {
                    // Include first 500 chars for debugging (avoid huge logs)
                    responseText: responseText.slice(0, 500),
                    parseError: parseError instanceof Error ? parseError.message : 'Unknown parse error',
                    httpStatus: response.status,
                  }
                }
              );
            }

            // Check for HTTP error status codes
            if (!response.ok) {
              // Server returned an error status
              const isServerError = response.status >= 500 && response.status < 600;

              if (isServerError) {
                throw new ApiError(
                  `Server error: ${response.status}`,
                  ERROR_CODES.INTERNAL_ERROR,
                  {
                    status: response.status,
                    userMessage: 'Our servers are experiencing issues. Please try again in a few moments.',
                    details: { responseData: json }
                  }
                );
              }

              // Client error (4xx)
              const errorCode = response.status === 401 ? ERROR_CODES.UNAUTHORIZED :
                response.status === 403 ? ERROR_CODES.FORBIDDEN :
                  response.status === 404 ? ERROR_CODES.NOT_FOUND :
                    response.status === 400 ? ERROR_CODES.VALIDATION_ERROR :
                      ERROR_CODES.INTERNAL_ERROR;

              throw new ApiError(
                `Request failed: ${response.status}`,
                errorCode,
                {
                  status: response.status,
                  userMessage: 'Unable to complete your request. Please try again.',
                  details: { responseData: json }
                }
              );
            }

            // Return raw response (legacy format)
            return json as T;
          } catch (error) {
            if (isTimeoutErrorUtil(error)) {
              throw new ApiError(
                'Request timed out',
                ERROR_CODES.TIMEOUT_ERROR,
                { userMessage: 'The request took too long. Please try again.' }
              );
            }

            // Enhanced network error detection and messaging
            const errorMessage = error instanceof Error ? error.message : String(error);
            const errorMessageLower = errorMessage.toLowerCase();

            const isConnectionRefused = errorMessageLower.includes('econnrefused') ||
              errorMessageLower.includes('connection refused');
            const isFailedToFetch = errorMessageLower.includes('failed to fetch');
            const isNetworkError = errorMessageLower.includes('fetch') ||
              errorMessageLower.includes('network') ||
              error instanceof TypeError; // fetch() network errors are TypeError

            // Connection refused - server is down or unreachable
            if (isConnectionRefused) {
              throw new ApiError(
                'Unable to reach the server',
                ERROR_CODES.NETWORK_ERROR,
                {
                  userMessage: 'Unable to reach the server. The service may be temporarily unavailable.',
                  status: 0 // Indicates network-level failure
                }
              );
            }

            // Generic "Failed to fetch" from browser - provide better message
            if (isFailedToFetch) {
              throw new ApiError(
                'Network connection failed',
                ERROR_CODES.NETWORK_ERROR,
                {
                  userMessage: 'Unable to connect to the server. Please check your internet connection and try again.',
                  status: 0
                }
              );
            }

            // Other network errors
            if (isNetworkError) {
              throw new ApiError(
                'Network error occurred',
                ERROR_CODES.NETWORK_ERROR,
                {
                  userMessage: 'Network connection error. Please check your internet connection and try again.',
                  status: 0
                }
              );
            }

            // Unknown error type - still provide helpful message
            throw new ApiError(
              'Connection error',
              ERROR_CODES.NETWORK_ERROR,
              {
                userMessage: 'Unable to connect to the server. Please try again.',
                status: 0
              }
            );
          } finally {
            cleanupTimeout();
          }
        },
        maxRetries,
        (error) => {
          if (error instanceof ApiError) {
            if (error.status !== undefined) {
              return isRetryableStatus(error.status);
            }
            return error.code === ERROR_CODES.NETWORK_ERROR ||
              error.code === ERROR_CODES.TIMEOUT_ERROR;
          }
          return false;
        }
      );
    });
  } catch (error) {
    // Log error and re-throw
    const duration = Date.now() - startTime;
    const apiError = error instanceof ApiError ? error : new ApiError(
      error instanceof Error ? error.message : 'Unknown error',
      ERROR_CODES.INTERNAL_ERROR
    );

    logErrorToMonitoring(apiError, {
      ...monitoringContext,
      status: apiError.status,
      attempt: attempts,
      durationMs: duration,
    });

    if (!IS_PRODUCTION) {
      console.error(
        `[LegacyApiClient] ❌ Failed after ${attempts} attempt(s)`,
        `\n  Error: ${apiError.message}`,
        `\n  Duration: ${duration}ms`,
        `\n  ⚠️  ENDPOINT NEEDS MIGRATION!`
      );
    }

    throw apiError;
  }
}

// ============================================================================
// Public API (Legacy)
// ============================================================================

/**
 * GET request (legacy format)
 * 
 * @deprecated - Use apiClient.get() for new endpoints
 * @param endpoint - API endpoint
 * @param options - Request options
 * @returns Raw legacy response data
 */
async function get<T extends LegacyResponse<unknown>>(
  endpoint: string,
  options?: RequestOptions
): Promise<T> {
  return request<T>('GET', endpoint, undefined, options);
}

/**
 * POST request (legacy format)
 * 
 * @deprecated - Use apiClient.post() for new endpoints
 */
async function post<T extends LegacyResponse<unknown>>(
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<T> {
  return request<T>('POST', endpoint, body, options);
}

/**
 * PUT request (legacy format)
 * 
 * @deprecated - Use apiClient.put() for new endpoints
 */
async function put<T extends LegacyResponse<unknown>>(
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<T> {
  return request<T>('PUT', endpoint, body, options);
}

/**
 * PATCH request (legacy format)
 * 
 * @deprecated - Use apiClient.patch() for new endpoints
 */
async function patch<T extends LegacyResponse<unknown>>(
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<T> {
  return request<T>('PATCH', endpoint, body, options);
}

/**
 * DELETE request (legacy format)
 * 
 * @deprecated - Use apiClient.delete() for new endpoints
 */
async function deleteRequest<T extends LegacyResponse<unknown>>(
  endpoint: string,
  options?: RequestOptions
): Promise<T> {
  return request<T>('DELETE', endpoint, undefined, options);
}

/**
 * Legacy API client
 * 
 * **USE ONLY FOR ENDPOINTS STILL IN LEGACY FORMAT**
 * 
 * This client is used while migrating endpoints from the old response format
 * to the new ApiResponse<T> format.
 * 
 * Track your migration progress in the endpoint migration status section above.
 * 
 * @deprecated - Delete this when all endpoints migrated to new format
 * @example
 * ```typescript
 * // Endpoint still returns { items: [...], total: N }
 * const response = await legacyApiClient.get<LegacyPaginatedResponse<Booking>>('/bookings');
 * const { items, total } = response;
 * ```
 */
export const legacyApiClient = {
  get,
  post,
  put,
  patch,
  delete: deleteRequest,
  request,
} as const;
